/**
 * Derives one visibility-matrix cell from the RAW stored `ViewVisibility`:
 * is the object shown in the given view, and which Kit/Group Member Rows
 * blanking applies there. Consumed by the visibility matrix grid.
 * The shown bit must agree with `filterVisibleColumns`, not merely with
 * `isVisibleInVisibilityMode`: the two diverge on the stored `hidden`
 * sentinel, which drops the column from every non-internal view and outranks
 * any view id beside it, so `['hidden', 'pdfview']` is NOT shown in pdfview.
 * That case resolves non-editable — nothing on this branch authors or clears
 * the sentinel. Never derive from a pool-filtered list — `['internal']`
 * filters to `[]` yet hides the object in every non-internal view. The
 * `internal` view resolves non-editable and always shown, sentinel included.
 */
import type { VisibilityTypeId } from '#types';
import { isVisibleInVisibilityMode } from '../rendering/isVisibleInVisibilityMode';
import { isBundleMemberCellSuppressed } from '../ArTable/components/ArCell/isBundleMemberCellSuppressed';
import { hiddenColumnSentinel } from './hiddenColumnSentinel';
import type { MemberRowsScope, ViewVisibility, VisibilityCellState } from './ViewVisibility.types';

export const resolveVisibilityCellState = (
    value: ViewVisibility,
    viewId: VisibilityTypeId,
): VisibilityCellState => {
    if (viewId === 'internal') return { editable: false, lock: 'internalView', shown: true, memberRows: 'none' };
    if ((value.visibilityTypes ?? []).includes(hiddenColumnSentinel)) {
        return { editable: false, lock: 'hiddenColumn', shown: false, memberRows: 'none' };
    }
    const shown = isVisibleInVisibilityMode({
        visibilityMode: viewId,
        visibilityTypes: value.visibilityTypes,
    });
    const kit = isBundleMemberCellSuppressed('member', 'kit', viewId, value);
    const group = isBundleMemberCellSuppressed('member', 'group', viewId, value);
    const memberRows: MemberRowsScope = kit && group ? 'both' : kit ? 'kit' : group ? 'group' : 'none';
    return { editable: true, shown, memberRows };
};
